import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from './Header';
import Footer from './Footer';
import './Home.css';
import { fetchFirstFnolJson } from '../utils/fnolApi';
import { openDocumentUploadBucket } from '../utils/documentUploadLink';

const workCategories = [
  'Alerts',
  'Inquiry',
  'Coverage',
  'Liability',
  'Litigation',
  'Damage',
  'Subrogation',
  'SIU',
];

const alertCategories = workCategories.filter((category) => category !== 'Alerts');

const normalizeAlertCategory = (value = '') => {
  const normalized = String(value || '')
    .toLowerCase()
    .replace(/[_-]+/g, ' ')
    .trim();

  if (!normalized) return '';
  if (/\b(enquiry|inquiry|correspondence)\b/.test(normalized)) return 'Inquiry';
  if (/\b(coverage|overage)\b/.test(normalized)) return 'Coverage';
  if (/\bliability\b/.test(normalized)) return 'Liability';
  if (/\blitigation\b/.test(normalized)) return 'Litigation';
  if (/\bdamage\b/.test(normalized)) return 'Damage';
  if (/\bsubrogation\b/.test(normalized)) return 'Subrogation';
  if (/\b(siu|sui)\b/.test(normalized)) return 'SIU';
  return '';
};

const getClaimCategory = (claim = {}) =>
  normalizeAlertCategory(
    claim.category ||
      claim.alert_category ||
      claim.alertCategory ||
      claim.alert_type ||
      claim.alertType ||
      claim.type ||
      claim.defect_category ||
      ''
  );

const getClaimCategories = (claim = {}) => {
  const candidates = [
    ...(Array.isArray(claim.categories) ? claim.categories : []),
    claim.primaryCategory,
    claim.category,
    claim.alert_category,
    claim.alertCategory,
    claim.alert_type,
    claim.alertType,
    claim.type,
    claim.defect_category,
  ];

  return Array.from(
    new Set(candidates.map(normalizeAlertCategory).filter(Boolean))
  );
};

const buildCategoryCounts = (claims = []) =>
  claims.reduce((counts, claim) => {
    getClaimCategories(claim).forEach((category) => {
      if (!alertCategories.includes(category)) return;
      counts[category] = (counts[category] || 0) + 1;
    });
    return counts;
  }, Object.fromEntries(alertCategories.map((category) => [category, 0])));

const getTotalAlertCount = (counts = {}) =>
  alertCategories.reduce(
    (total, category) => total + (Number(counts[category]) || 0),
    0
  );

const categoryToneClass = {
  Inquiry: 'tone-coral',
  Coverage: 'tone-gold',
  Liability: 'tone-orange',
  Litigation: 'tone-pink',
  Damage: 'tone-cyan',
  Subrogation: 'tone-amber',
  SIU: 'tone-red',
};

const formatDate = (date) =>
  date.toLocaleDateString([], {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });

const formatTime = (date) =>
  date.toLocaleTimeString([], {
    hour: '2-digit',
    minute: '2-digit',
  });

const getClaimAge = (claim) => {
  const createdAt = claim.created_at || claim.update_date || claim.date_of_loss || claim.processed_at;
  const createdTime = createdAt ? new Date(createdAt).getTime() : Date.now();
  if (Number.isNaN(createdTime)) return '0d';

  const days = Math.max(0, Math.floor((Date.now() - createdTime) / 86400000));
  return `${days}d`;
};

const getAlertPriority = (category = '') => {
  const normalizedCategory = normalizeAlertCategory(category).toLowerCase();

  if (['litigation', 'siu', 'liability'].includes(normalizedCategory)) return 'High';
  if (['damage', 'subrogation', 'overage'].includes(normalizedCategory)) return 'Medium';
  if (['inquiry', 'coverage'].includes(normalizedCategory)) return 'Low';
  return '';
};

const priorityRank = {
  High: 3,
  Medium: 2,
  Low: 1,
};

const parseDays = (value) => {
  const match = String(value || '').match(/\d+/);
  return match ? Number(match[0]) : 0;
};

const getClaimAgeDays = (claim) => parseDays(getClaimAge(claim));

const getClaimWaitingDays = (claim) => parseDays(claim.waiting || claim.waiting_time);

const getPriorityScore = (claim, priority) => {
  const baseScore = {
    High: 90,
    Medium: 60,
    Low: 30,
  }[priority] || 0;
  const ageingScore = Math.min(getClaimAgeDays(claim), 9);
  const waitingScore = Math.min(getClaimWaitingDays(claim), 9);

  return Math.min(99, baseScore + ageingScore + waitingScore);
};

const getClaimKey = (claim) => claim.claim_id || claim.event_id;

const getFnolIdFromClaim = (claimId = '') =>
  typeof claimId === 'string' && claimId.startsWith('C-')
    ? claimId.replace(/^C-/, 'F-')
    : '';

const getClaimIdFromFnol = (record = {}) => {
  if (record.claim_id) return record.claim_id;
  if (typeof record.fnol_id === 'string' && record.fnol_id.startsWith('F-')) {
    return record.fnol_id.replace(/^F-/, 'C-');
  }
  return '';
};

const hiddenClaimIds = new Set(['C-260821-006', 'C-260831-001']);
const isHiddenClaimId = (claimId = '') => hiddenClaimIds.has(claimId);

const getClaimFnolId = (claim = {}) =>
  claim.fnol_id || claim.fnolId || getFnolIdFromClaim(claim.claim_id) || 'No FNOL id';

const inferFallbackCategory = (record = {}, claim = {}) => {
  const status = String(record.status || claim.form_status || '').toLowerCase();
  const text = [
    record.loss_description,
    claim.loss_description,
    claim.loss_summary,
    claim.damage_description,
  ]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();

  if (status.includes('not_filled') || status.includes('not filled')) return 'Inquiry';
  if (/\b(storm|roof|tree|property|damage)\b/.test(text)) return 'Damage';
  if (/\b(slip|fall|fell|ice|injur|floor|walkway|hip|arm)\b/.test(text)) return 'Liability';
  return 'Inquiry';
};

const buildFallbackWorkUpdateFromFnol = (record = {}, claim = {}) => {
  const claimId = getClaimIdFromFnol(record) || claim.claim_id || '';
  if (!claimId) return null;

  const formStatus = record.status || claim.form_status || 'Pending';
  const category = inferFallbackCategory(record, claim);

  return {
    claim_id: claimId,
    fnol_id: record.fnol_id || claim.fnol_id || getFnolIdFromClaim(claimId),
    customer_name: record.customer_name || claim.customer_name || '',
    loss_type: claim.loss_type || (category === 'Damage' ? 'PROPERTY' : 'GENERAL LIABILITY'),
    loss_description: record.loss_description || claim.loss_description || claim.damage_description || '',
    exposure_amount:
      claim.exposure_amount ||
      claim.estimated_financial_exposure ||
      claim.probable_amount_entire_loss ||
      '',
    loss_summary:
      record.loss_description ||
      claim.loss_summary ||
      claim.loss_description ||
      'Loss summary will appear when claim details are available.',
    claim_summary:
      claim.claim_summary ||
      `Claim Summary: Status: ${claim.claim_status || 'PENDING'} Summary: ${formStatus.replace(/_/g, ' ')}`,
    created_at: claim.created_at || record.processed_at || claim.updated_at || '',
    waiting: '0d',
    category,
    event_id: claimId,
  };
};

const formatSummaryValue = (value) => {
  if (Array.isArray(value)) {
    return value
      .map(formatSummaryValue)
      .filter(Boolean)
      .join(' ');
  }

  if (value && typeof value === 'object') {
    return Object.entries(value)
      .map(([key, child]) => {
        const text = formatSummaryValue(child);
        return text ? `${key.replaceAll('_', ' ')}: ${text}` : '';
      })
      .filter(Boolean)
      .join(' ');
  }

  return String(value || '')
    .replace(/\*\*/g, '')
    .replace(/\s+/g, ' ')
    .trim();
};

const getFirstFieldValue = (record, keys) => {
  for (const key of keys) {
    const value = record?.[key];
    const formattedValue = formatSummaryValue(value);
    if (formattedValue) {
      return formattedValue;
    }
  }
  return '';
};

const formatCurrencyAmount = (value) => {
  const text = formatSummaryValue(value);
  if (!text) return '';

  const labeledMatch = text.match(
    /Estimated\s+Financial\s+Exposure\s*:\s*(\$?\s*[\d,]+(?:\.\d{1,2})?)/i
  );
  const rawAmount =
    labeledMatch?.[1] || text.match(/\$\s*[\d,]+(?:\.\d{1,2})?/)?.[0] || '';

  if (!rawAmount) return '';

  const normalized = rawAmount.replace(/\s+/g, '');
  const numericValue = Number(normalized.replace(/[$,]/g, ''));
  return Number.isFinite(numericValue)
    ? numericValue.toLocaleString('en-US', {
        style: 'currency',
        currency: 'USD',
        maximumFractionDigits: 0,
      })
    : '';
};

const getExposureAmount = ({ claim = {}, formData = {}, insights = {} } = {}) => {
  const combinedRecord = { ...claim, ...formData, ...insights };
  const directAmount = getFirstFieldValue(combinedRecord, [
    'estimated_financial_exposure',
    'Estimated_Financial_Exposure',
    'exposure_amount',
    'probable_amount_entire_loss',
  ]);
  const directCurrency = formatCurrencyAmount(directAmount);

  if (directCurrency) return directCurrency;

  return formatCurrencyAmount(
    combinedRecord.Exposure_Determination_Agent_Insights ||
      combinedRecord.Exposure_Determination_Agent_Next_Best_Action
  );
};

const getLossSummary = (claim) =>
  getFirstFieldValue(claim, [
    'loss_summary',
    'loss_description',
    'lossSummary',
    'loss_location',
    'description',
    'What_came_in',
  ]) || 'Loss summary will appear when claim details are available.';

const getClaimSummary = (claim) =>
  getFirstFieldValue(claim, [
    'claim_summary',
    'claim_summary_checker_insights',
    'Claim_Summary_Agent_Insights',
    'Claim_Summary_Checker_Updater_Agent_Insights',
    'claimSummary',
  ]) || 'Claim summary will appear when claim insight is available.';

const workUpdateMappings = [
  {
    category: 'Inquiry',
    keys: [
      'Notice_Of_Loss_Agent_Insights',
      'FNOL_Reviewer_Agent_Insights',
      'Document_Intake_Validator_Agent_Insights',
      'Document_Intake_Classifier_Agent_Insights',
      'External_Communicator_Agent_Insights',
    ],
  },
  {
    category: 'Coverage',
    keys: [
      'Coverage_Agent_Insights',
      'Policy_Checker_Agent_Insights',
      'Eligibility_Coverage_Analyst_Agent_Insights',
    ],
  },
  {
    category: 'Liability',
    keys: ['Liability_Agent_Insights'],
  },
  {
    category: 'Litigation',
    keys: ['Litigation_Agent_Insights', 'Legal_Record_Reviewer_Agent_Insights'],
  },
  {
    category: 'Damage',
    keys: [
      'Damage_Agent_Insights',
      'Medical_Records_Reviewer_Agent_Insights',
      'Surveillance_Security_Video_Reviewer_Agent_Insights',
      'Maintenance_Inspection_Records_Reviewer_Agent_Insights',
      'Bills_Invoices_Analyzer_Agent_Insights',
    ],
  },
  {
    category: 'Subrogation',
    keys: [
      'Subrogation_Agent_Insights',
      'Witness_Reviewer_Agent_Insights',
      'Lease_Contract_Document_Reviewer_Agent_Insights',
      'Prior_Claim_History_Loss_Report_Verifier_Agent_Insights',
      'Demand_Letter_Agent_Insights',
    ],
  },
  {
    category: 'SIU',
    keys: [
      'SUI_Agent_Insights',
      'Fraud_Detection_Agent_Insights',
      'Fraud_Compliance_Detector_Agent_Insights',
    ],
  },
];

const buildWorkUpdatesFromClaimDetail = (detail) => {
  const claim = detail?.claim || {};
  const automation = detail?.automation || {};
  const insights = detail?.insights || {};
  const formData = detail?.form_data || {};

  const baseRecord = {
    claim_id: claim.claim_id || automation.claim_id || formData.claim_id || '',
    fnol_id: automation.fnol_id || claim.fnol_id || formData.fnol_id || '',
    customer_name:
      claim.insured_full_name ||
      claim.customer_name ||
      formData.insured_name ||
      formData.insured ||
      detail?.insured_name ||
      '',
    loss_type: formData.loss_type || claim.loss_type || '',
    loss_description: formData.loss_description || claim.damage_description || '',
    exposure_amount: getExposureAmount({ claim, formData, insights }),
    loss_summary: formData.loss_description || claim.damage_description || '',
    claim_summary:
      insights.claim_summary_checker_insights ||
      insights.Claim_Summary_Agent_Insights ||
      '',
    created_at:
      automation.processed_at ||
      claim.updated_at ||
      claim.created_at ||
      automation.created_at ||
      '',
    waiting: '0d',
  };

  return workUpdateMappings.flatMap((mapping) => {
    const insightText = getFirstFieldValue(insights, mapping.keys);
    if (!insightText) return [];

    return [
      {
        ...baseRecord,
        category: mapping.category,
        event_id: `${baseRecord.claim_id || 'claim'}-${mapping.category.toLowerCase()}`,
        insight_text: insightText,
      },
    ];
  });
};

const mergeUpdatesByClaim = (updates = []) => {
  const merged = new Map();

  updates.forEach((update) => {
    const claimKey = getClaimKey(update);
    if (!claimKey) return;

    const priority = getAlertPriority(update.category);
    const existing = merged.get(claimKey);

    if (!existing) {
      merged.set(claimKey, {
        ...update,
        categories: [update.category].filter(Boolean),
        primaryCategory: update.category,
      });
      return;
    }

    const categories = Array.from(
      new Set([...existing.categories, update.category].filter(Boolean))
    );
    const existingPriority = getAlertPriority(existing.primaryCategory || existing.category);

    if ((priorityRank[priority] || 0) > (priorityRank[existingPriority] || 0)) {
      merged.set(claimKey, {
        ...existing,
        ...update,
        categories,
        primaryCategory: update.category,
        category: update.category,
        event_id: claimKey,
      });
      return;
    }

    merged.set(claimKey, {
      ...existing,
      categories,
      event_id: claimKey,
    });
  });

  return Array.from(merged.values());
};

const MyWork = ({ onLogout }) => {
  const navigate = useNavigate();
  const [updates, setUpdates] = useState([]);
  const [categoryCounts, setCategoryCounts] = useState({});
  const [totalUpdates, setTotalUpdates] = useState(0);
  const [now, setNow] = useState(new Date());
  const [lastRefreshedAt, setLastRefreshedAt] = useState(null);
  const [loadError, setLoadError] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('Alerts');
  const [queueSort, setQueueSort] = useState('Priority');
  const [expandedAlertCards, setExpandedAlertCards] = useState({});

  useEffect(() => {
    const timer = window.setInterval(() => setNow(new Date()), 1000);
    return () => window.clearInterval(timer);
  }, []);

  useEffect(() => {
    let active = true;

    const loadUpdates = async () => {
      try {
        const [claimResult, fnolResult] = await Promise.all([
          fetchFirstFnolJson('/claim-records'),
          fetchFirstFnolJson('/fnol-records').catch(() => ({ records: [] })),
        ]);
        const claimRecords = (Array.isArray(claimResult.records) ? claimResult.records : [])
          .filter((record) => !isHiddenClaimId(record.claim_id));
        const fnolRecords = (Array.isArray(fnolResult.records) ? fnolResult.records : [])
          .filter((record) => !isHiddenClaimId(getClaimIdFromFnol(record)));
        const claimById = new Map(claimRecords.map((record) => [record.claim_id, record]));
        const claimDetails = await Promise.all(
          fnolRecords.map((record) => {
            const claimId = getClaimIdFromFnol(record);
            if (!claimId) return Promise.resolve(null);
            return fetchFirstFnolJson(`/claim-records/${encodeURIComponent(claimId)}`).catch(() => null);
          })
        );

        const rawUpdates = fnolRecords.flatMap((record, index) => {
          const claimId = getClaimIdFromFnol(record);
          const claimRecord = claimById.get(claimId) || {};
          const detail = claimDetails[index];
          const detailUpdates = detail ? buildWorkUpdatesFromClaimDetail(detail) : [];

          return detailUpdates.length
            ? detailUpdates
            : [buildFallbackWorkUpdateFromFnol(record, claimRecord)].filter(Boolean);
        });

        const normalizedUpdates = rawUpdates
          .map((claim) => ({
            ...claim,
            category: getClaimCategory(claim) || claim.category || '',
          }))
          .filter((claim) => getClaimCategory(claim));
        const mergedUpdates = mergeUpdatesByClaim(normalizedUpdates);
        const computedCategoryCounts = buildCategoryCounts(mergedUpdates);

        if (active) {
          setUpdates(mergedUpdates);
          setCategoryCounts(computedCategoryCounts);
          setTotalUpdates(getTotalAlertCount(computedCategoryCounts));
          setLoadError('');
          setLastRefreshedAt(new Date());
        }
      } catch (error) {
        if (active) setLoadError(error.message);
      }
    };

    loadUpdates();
    const refresh = window.setInterval(loadUpdates, 10000);

    return () => {
      active = false;
      window.clearInterval(refresh);
    };
  }, []);

  const nextRefreshIn = lastRefreshedAt
    ? Math.max(0, 10 - Math.floor((now.getTime() - lastRefreshedAt.getTime()) / 1000))
    : 10;

  const filteredUpdates = useMemo(() => {
    if (selectedCategory === 'Alerts') return updates;
    return updates.filter((claim) => getClaimCategories(claim).includes(selectedCategory));
  }, [selectedCategory, updates]);

  const workQueue = useMemo(() => {
    const queue = filteredUpdates
      .filter((claim) => getClaimKey(claim))
      .map((claim) => {
        const displayCategory =
          selectedCategory !== 'Alerts' && getClaimCategories(claim).includes(selectedCategory)
            ? selectedCategory
            : claim.category;
        const priority = getAlertPriority(displayCategory);
        return {
          ...claim,
          displayCategory,
          priority,
          score: getPriorityScore(claim, priority),
        };
      });

    return queue.sort((a, b) => {
      if (queueSort === 'Ageing') {
        return getClaimAgeDays(b) - getClaimAgeDays(a) || b.score - a.score;
      }

      if (queueSort === 'Waiting') {
        return getClaimWaitingDays(b) - getClaimWaitingDays(a) || b.score - a.score;
      }

      return b.score - a.score;
    });
  }, [filteredUpdates, queueSort, selectedCategory]);

  return (
    <div className="home-page">
      <Header
        rightContent={
          <div className="header-date-time">
            <strong>Date: {formatDate(now)}</strong>
            <strong>Time: {formatTime(now)}</strong>
          </div>
        }
      />
      <div className="page-body">
        <aside className="sidebar mini-sidebar">
          <nav className="sidebar-nav">
            <button onClick={() => navigate('/home')} className="nav-item">
              <i className="fas fa-home"></i><span className="nav-label">Home</span>
            </button>
            <button onClick={() => navigate('/my-work')} className="nav-item active">
              <i className="fas fa-briefcase"></i><span className="nav-label">My Work</span>
            </button>
            <button onClick={() => navigate('/crm')} className="nav-item">
              <i className="fas fa-address-book"></i><span className="nav-label">FNOL</span>
            </button>
            <button onClick={() => navigate('/claims')} className="nav-item">
              <i className="fas fa-file-shield"></i><span className="nav-label">Claim</span>
            </button>
            <button onClick={() => navigate('/ops-dashboard')} className="nav-item">
              <i className="fas fa-tachometer-alt"></i><span className="nav-label">Ops Dashboard</span>
            </button>
            <button onClick={() => navigate('/claims?tab=quality-score')} className="nav-item quality-score-nav-item">
              <i className="fas fa-clipboard-check"></i><span className="nav-label">QA</span>
            </button>
            <button onClick={() => navigate('/agent-dashboard')} className="nav-item">
              <i className="fas fa-gauge-high"></i><span className="nav-label">AI Agent Dashboard</span>
            </button>
            <button onClick={openDocumentUploadBucket} className="nav-item">
              <i className="fas fa-file-arrow-up"></i><span className="nav-label">Document Upload</span>
            </button>
          </nav>
          <div className="sidebar-footer">
            <button onClick={onLogout} className="nav-item logout-btn">
              <i className="fas fa-sign-out-alt"></i><span className="nav-label">Logout</span>
            </button>
          </div>
        </aside>

        <main className="home-main my-work-main cockpit-work-main" aria-label="My Work">
          <section className="work-surface cockpit-work-surface">
            <section className="cockpit-kpi-grid" aria-label="Work category summary">
              {workCategories.map((category) => {
                const isSelected = selectedCategory === category;
                return (
                  <button
                    type="button"
                    key={category}
                    className={`cockpit-kpi-card ${category === 'Alerts' ? 'alerts' : ''} ${isSelected ? 'active' : ''}`}
                    onClick={() => setSelectedCategory(category)}
                    onPointerDown={() => setSelectedCategory(category)}
                    onMouseDown={() => setSelectedCategory(category)}
                    aria-pressed={isSelected}
                    data-selected={isSelected ? 'true' : 'false'}
                    style={isSelected ? { '--kpi-selected': '1' } : undefined}
                  >
                    <strong
                      className={category !== 'Alerts' ? categoryToneClass[category] || '' : ''}
                    >
                      {category === 'Alerts' ? (loadError ? '0' : totalUpdates) : categoryCounts[category] ?? 0}
                    </strong>
                    <span>{category === 'Alerts' ? 'Total Alert' : category}</span>
                  </button>
                );
              })}
            </section>

            <section className="cockpit-work-grid">
              <section className="cockpit-panel cockpit-queue-panel">
                <div className="cockpit-panel-title">Prioritized Work Queue</div>
                <div className="cockpit-tabs" aria-label="Queue sort options">
                  {['Priority', 'Ageing', 'Waiting'].map((sortName) => (
                    <button
                      type="button"
                      key={sortName}
                      className={queueSort === sortName ? 'active' : ''}
                      onClick={() => setQueueSort(sortName)}
                    >
                      {sortName}
                    </button>
                  ))}
                </div>

                <div className="cockpit-queue-list">
                  {workQueue.map((claim) => {
                    const priority = claim.priority || 'Low';
                    const claimCardKey = getClaimKey(claim) || claim.event_id || 'claim';
                    const claimAlertCategories =
                      selectedCategory === 'Alerts'
                        ? getClaimCategories(claim)
                        : [claim.displayCategory || claim.category].filter(Boolean);
                    const isAlertCardExpanded = Boolean(expandedAlertCards[claimCardKey]);
                    const visibleAlertCategories = isAlertCardExpanded
                      ? claimAlertCategories
                      : claimAlertCategories.slice(0, 3);
                    const hiddenAlertCount = Math.max(0, claimAlertCategories.length - visibleAlertCategories.length);
                    const activeMetric =
                      queueSort === 'Ageing'
                        ? { value: `${getClaimAgeDays(claim)}d`, label: 'ageing' }
                        : queueSort === 'Waiting'
                          ? { value: `${getClaimWaitingDays(claim)}d`, label: 'waiting' }
                          : { value: claim.score, label: 'priority' };
                    return (
                      <article
                        className={`cockpit-claim-card priority-${priority.toLowerCase()} ${isAlertCardExpanded ? 'cockpit-claim-card-expanded' : ''}`}
                        key={claim.event_id || getClaimKey(claim)}
                      >
                        <div className="cockpit-score">
                          <strong>{activeMetric.value}</strong>
                          <span>{activeMetric.label}</span>
                        </div>
                        <div className="cockpit-claim-copy">
                          <div className="cockpit-field-line">
                            <span>Claim</span>
                            <button
                              type="button"
                              onClick={() => {
                                const claimId = claim.claim_id || getClaimKey(claim);
                                navigate(
                                  claimId
                                    ? `/claims?claimId=${encodeURIComponent(claimId)}`
                                    : '/claims',
                                  { state: { claimId } }
                                );
                              }}
                            >
                              {claim.claim_id || 'No claim id'}
                            </button>
                          </div>
                          <div className="cockpit-field-line">
                            <span>FNOL</span>
                            <strong>{getClaimFnolId(claim)}</strong>
                          </div>
                          <div className="cockpit-field-line">
                            <span>Insured</span>
                            <strong>{claim.customer_name || 'Unknown insured'}</strong>
                          </div>
                          <div className="cockpit-field-line">
                            <span>Loss</span>
                            <strong>{claim.loss_type || claim.lossType || 'General Liability'}</strong>
                          </div>
                        </div>
                        <div className="cockpit-chip-row cockpit-alert-chip-list">
                          {(visibleAlertCategories.length ? visibleAlertCategories : ['Alert']).map((category) => (
                            <span
                              className={`cockpit-chip alert alert-${(getAlertPriority(category) || priority || 'Low').toLowerCase()}`}
                              key={`${getClaimKey(claim)}-${category}`}
                            >
                              {category} alert
                            </span>
                          ))}
                          {(hiddenAlertCount > 0 || isAlertCardExpanded) && (
                            <button
                              type="button"
                              className="cockpit-chip alert-more"
                              onClick={() =>
                                setExpandedAlertCards((current) => ({
                                  ...current,
                                  [claimCardKey]: !current[claimCardKey],
                                }))
                              }
                            >
                              {isAlertCardExpanded ? 'Show less' : `+${hiddenAlertCount} more`}
                            </button>
                          )}
                        </div>
                        <div className="cockpit-claim-side">
                          <strong>{claim.exposure_amount || '$1,000'}</strong>
                          <span>{claim.waiting || claim.waiting_time || '0d'} waiting</span>
                        </div>
                        <div className="cockpit-summary-band">
                          <div>
                            <span>Loss summary</span>
                            <p>{getLossSummary(claim)}</p>
                          </div>
                          <div>
                            <span>Claim summary</span>
                            <p>{getClaimSummary(claim)}</p>
                          </div>
                        </div>
                      </article>
                    );
                  })}
                  {!workQueue.length && (
                    <div className="cockpit-empty-state">
                      {loadError || 'No claim updates yet.'}
                    </div>
                  )}
                </div>
                <div className="cockpit-logic-card cockpit-logic-card-inline">
                  <h2>Priority Logic</h2>
                  <div>
                    <span><strong className="priority-high">High</strong> Litigation, SIU, Liability</span>
                    <span><strong className="priority-medium">Medium</strong> Damage, Subrogation</span>
                    <span><strong className="priority-low">Low</strong> Inquiry, Coverage</span>
                  </div>
                </div>
              </section>
            </section>
          </section>
        </main>
      </div>
      <Footer
        rightContent={
          <span className="refresh-status work-refresh-footer">
            {lastRefreshedAt
              ? `Last refreshed ${formatTime(lastRefreshedAt)} • Next in ${nextRefreshIn}s`
              : 'Refreshing every 10s'}
          </span>
        }
      />
    </div>
  );
};

export default MyWork;
